import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw, Save } from 'lucide-react';
import { adminService } from '../services/adminService';
import { AdminSetting } from '../types';
import { useToast } from '../contexts/ToastContext';
import { PLATFORM_SETTING_KEYS } from '../utils/platformSettings';

// Economy controls have their own guarded panel. Keeping them out of this
// editor prevents the old catch-all route from becoming a second write path.
const ECONOMY_SETTING_KEYS = new Set([
  'free_passes_per_day',
  'max_earned_pass_held',
  'passes_reset_hour_utc',
  'basic_allocation_tp',
  'premium_allocation_tp',
  'pro_allocation_tp',
  'tp_per_ngn',
  'tp_per_gbp',
  'tp_per_ghs',
  'tp_per_cad',
  'tp_per_usd',
  'tp_per_inr',
  'tp_per_aud',
  'tp_per_eur',
  'tp_per_kes',
  'tp_per_zar',
]);

// These are the API-backed settings that belong to the Global Settings tab.
// Platform withdrawal controls are intentionally owned by PlatformSettingsPanel
// so an administrator cannot edit the same setting through two surfaces.
const SETTING_GROUPS: Record<string, string[]> = {
  'Standard Challenge': [
    'standard_challenge_timer_seconds',
    'standard_band_gt20',
    'standard_band_15to20',
    'standard_band_10to14',
    'standard_band_5to9',
    'standard_band_lte4',
    'standard_reveal_tool_timer_seconds',
    'standard_interim_offer_percentages',
    'standard_reveal_tool_offer_every_n',
    // Legacy names retained for installations that still have these rows.
    'band_size_tier_1',
    'band_size_tier_2',
    'band_size_tier_3',
    'band_size_tier_4',
    'band_size_tier_5',
    'blocks_remaining_tier_2_min',
    'blocks_remaining_tier_3_min',
    'blocks_remaining_tier_4_min',
    'blocks_remaining_tier_5_min',
  ],
  'Pro Workspace': [
    'pro_question_timer_seconds',
    'pro_question_timer_min_seconds',
    'pro_question_timer_max_seconds',
    'pro_question_timer_per_difficulty',
    'pro_banking_checkpoint_every_n',
    'pro_50_50_offer_every_n',
    'pro_safe_haven_at_question',
    'pro_ladder_values',
    'pro_num_questions',
    // Legacy names retained for installations that still have these rows.
    'pro_max_rungs',
  ],
  Leaderboard: [
    'leaderboard_ranking_metric',
    'leaderboard_reset_hour_utc',
  ],
  'Fraud Engine': [
    'fraud_ladder_shadow_mode',
    'fraud_ladder_thresholds',
    'fraud_ladder_ema_alpha',
    'fraud_ladder_min_sessions_before_action',
    'fraud_ladder_auto_decay_days',
    'fraud_ladder_throttle_factor',
    'fraud_engine_detectors_enabled',
    'fraud_engine_step_up_enabled',
    'fraud_engine_play_integrity_enabled',
  ],
  'Question Generation': [
    'question_gen_enabled',
    'question_gen_schedule_utc_hour',
    'question_gen_daily_volume',
    'question_gen_volume_per_category',
    'question_similarity_threshold',
    'question_confidence_auto_approve',
    'question_max_age_days',
    'question_difficulty_recalibration_correct_pct_threshold',
  ],
};

const GROUPED_KEYS = new Set(Object.values(SETTING_GROUPS).flat());

const parseSettingValue = (setting: AdminSetting, rawValue: string): unknown => {
  const raw = rawValue.trim();
  if (setting.value_type === 'int') {
    if (raw === '') throw new Error('Enter a whole number.');
    const value = Number(raw);
    if (!Number.isInteger(value)) throw new Error('Enter a whole number.');
    return value;
  }
  if (setting.value_type === 'float') {
    if (raw === '') throw new Error('Enter a number.');
    const value = Number(raw);
    if (!Number.isFinite(value)) throw new Error('Enter a finite number.');
    return value;
  }
  if (setting.value_type === 'bool') {
    if (raw !== 'true' && raw !== 'false') {
      throw new Error('Choose true or false.');
    }
    return raw === 'true';
  }
  if (setting.value_type === 'json') {
    try {
      return JSON.parse(rawValue);
    } catch {
      throw new Error('Enter valid JSON.');
    }
  }
  return rawValue;
};

const LegacyGlobalSettingsPanel: React.FC = () => {
  const toast = useToast();
  const [settings, setSettings] = useState<AdminSetting[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAdminSettings();
      setSettings(data);
      const nextDrafts: Record<string, string> = {};
      data.forEach(setting => { nextDrafts[setting.key] = setting.value; });
      setDrafts(nextDrafts);
      setFieldErrors({});
    } catch {
      setSettings([]);
      toast.error('Error', 'Failed to load global settings');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const visibleSettings = useMemo(
    () => settings.filter(
      setting => !ECONOMY_SETTING_KEYS.has(setting.key) && !PLATFORM_SETTING_KEYS.has(setting.key)
    ),
    [settings]
  );

  const handleDraftChange = (key: string, value: string) => {
    setDrafts(previous => ({ ...previous, [key]: value }));
    setFieldErrors(previous => {
      if (!previous[key]) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  };

  const handleSave = async (setting: AdminSetting) => {
    if (savingKey !== null) return;
    const rawValue = drafts[setting.key] ?? setting.value;
    let parsed: unknown;
    try {
      parsed = parseSettingValue(setting, rawValue);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Enter a valid value.';
      setFieldErrors(previous => ({ ...previous, [setting.key]: message }));
      return;
    }

    setSavingKey(setting.key);
    try {
      const saved = await adminService.updateAdminSetting(setting.key, parsed);
      setSettings(previous => previous.map(item => item.key === setting.key ? saved : item));
      setDrafts(previous => ({ ...previous, [setting.key]: saved.value }));
      toast.success('Setting Saved', `${setting.key} updated`);
    } catch {
      toast.error('Error', `Failed to save ${setting.key}`);
    } finally {
      setSavingKey(null);
    }
  };

  const renderTable = (groupSettings: AdminSetting[]) => (
    <div className="table-container">
      <table className="config-table">
        <thead>
          <tr>
            <th style={{ width: '28%' }}>Key</th>
            <th style={{ width: '12%' }}>Type</th>
            <th>Value</th>
            <th style={{ width: '18%' }}>Last updated</th>
            <th style={{ width: '80px' }}></th>
          </tr>
        </thead>
        <tbody>
          {groupSettings.map(setting => {
            const draft = drafts[setting.key] ?? setting.value;
            const isDirty = draft !== setting.value;
            const isSaving = savingKey === setting.key;
            const error = fieldErrors[setting.key];
            return (
              <tr key={setting.key}>
                <td>
                  <code style={{ fontSize: '12px' }}>{setting.key}</code>
                  {setting.description && (
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {setting.description}
                    </div>
                  )}
                  {error && <div role="alert" style={{ color: '#b91c1c', fontSize: '12px', marginTop: '4px' }}>{error}</div>}
                </td>
                <td><span className="currency-badge">{setting.value_type}</span></td>
                <td>
                  {setting.value_type === 'bool' ? (
                    <select
                      aria-label={`${setting.key} value`}
                      value={draft}
                      onChange={event => handleDraftChange(setting.key, event.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  ) : setting.value_type === 'json' ? (
                    <textarea
                      aria-label={`${setting.key} value`}
                      value={draft}
                      onChange={event => handleDraftChange(setting.key, event.target.value)}
                      rows={3}
                      style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px', resize: 'vertical' }}
                    />
                  ) : (
                    <input
                      aria-label={`${setting.key} value`}
                      type={setting.value_type === 'int' || setting.value_type === 'float' ? 'number' : 'text'}
                      step={setting.value_type === 'float' ? 'any' : '1'}
                      value={draft}
                      onChange={event => handleDraftChange(setting.key, event.target.value)}
                      style={{ width: '100%' }}
                    />
                  )}
                </td>
                <td style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  {setting.updated_by && <div>{setting.updated_by}</div>}
                  {setting.updated_at ? new Date(setting.updated_at).toLocaleDateString() : 'Not available'}
                </td>
                <td>
                  <button
                    type="button"
                    className="save-btn compact"
                    onClick={() => void handleSave(setting)}
                    disabled={!isDirty || savingKey !== null}
                    aria-label={`Save ${setting.key}`}
                  >
                    {isSaving ? <RefreshCw className="spin" size={14} /> : <Save size={14} />}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="config-content">
      <div className="section-header">
        <div>
          <h3>Global Settings</h3>
          <p style={{ color: '#6b7280', margin: '6px 0 0', fontSize: '14px' }}>
            Existing game, leaderboard, fraud, and question-generation controls.
            Economy controls are managed in the Economy Settings tab and withdrawal controls in
            the Platform Settings tab.
          </p>
        </div>
        <button onClick={() => void loadSettings()} className="refresh-btn" disabled={loading}>
          <RefreshCw className={loading ? 'spin' : ''} size={18} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <RefreshCw className="spin" size={32} />
          <span>Loading settings…</span>
        </div>
      ) : visibleSettings.length === 0 ? (
        <div className="config-unavailable-panel" role="status">
          <AlertTriangle size={20} />
          <div>
            <h3>No non-economy settings found</h3>
            <p>The authenticated settings API returned no existing controls for this panel.</p>
          </div>
        </div>
      ) : (
        <>
          {Object.entries(SETTING_GROUPS).map(([groupName, keys]) => {
            const groupSettings = keys
              .map(key => visibleSettings.find(setting => setting.key === key))
              .filter(Boolean) as AdminSetting[];
            if (groupSettings.length === 0) return null;
            return (
              <div key={groupName} className="settings-group">
                <h4 className="settings-group-title">{groupName}</h4>
                {renderTable(groupSettings)}
              </div>
            );
          })}
          {(() => {
            const ungrouped = visibleSettings.filter(setting => !GROUPED_KEYS.has(setting.key));
            if (ungrouped.length === 0) return null;
            return (
              <div className="settings-group">
                <h4 className="settings-group-title">Other</h4>
                {renderTable(ungrouped)}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default LegacyGlobalSettingsPanel;
